<script setup>
/**
 * 다음 달 미리보기 팝업 (PC)
 *
 * - 컴포넌트 마운트 시 변형(theme) 중 랜덤 선택
 * - 텍스트 동일, 컬러/일러스트만 다른 N가지 변형
 * - props.variantKey 로 외부 강제 지정 가능 (없으면 랜덤)
 */
import nextMonthGreenImg from '@manseImages/monthly-fortune/next-month-green.png';
import nextMonthLavenderImg from '@manseImages/monthly-fortune/next-month-lavender.png';
import { addContentLogAPI } from '@/api/logServiceApi';

const nl2br = (s) => (s == null ? '' : String(s).replaceAll('\n', '<br>'));

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  /** 다음 달 라벨 (예: "6월 갑오월(甲午月)") */
  nextMonthLabel: { type: String, default: '' },
  /** 강제 지정용 (없으면 랜덤) */
  variantKey: { type: String, default: null },
  /** 무료데이터의 6_다음달미리보기 섹션 */
  nextMonthData: { type: Object, default: null },
});

const STRATEGY_EMOJI = {
  직진형: '🚀',
  추진형: '🚀',
  준비형: '🌱',
  충전형: '🛌',
  균형형: '⚖️',
};

const dynamicBadge = computed(() => {
  const strategy = props.nextMonthData?.전략유형;
  if (!strategy) return null;
  const emoji = STRATEGY_EMOJI[strategy] || '🚀';
  return `${emoji} ${strategy}`;
});

/** API의 다음달 데이터 우선, 폴백으로 nextMonthLabel */
const nextMonthNumber = computed(() => {
  const fromData = props.nextMonthData?.다음달?.match(/(\d+)\s*월/);
  if (fromData) return fromData[1];
  const fromLabel = props.nextMonthLabel?.match(/(\d+)\s*월/);
  return fromLabel ? fromLabel[1] : '';
});

const ctaText = computed(() => {
  const m = nextMonthNumber.value;
  return m ? `자세한 이야기는 ${m}월 월운세에서 →` : '자세한 이야기는 다음 달 월운세에서 →';
});

const emit = defineEmits(['update:modelValue', 'goNextMonth']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

// 변형 정의 (Figma 시안 기준)
const VARIANTS = [
  {
    key: 'forward',
    bg: '#6caf4e',
    illust: nextMonthGreenImg,
    titleColor: '#fff',
    monthColor: '#fff',
    ctaColor: '#fff',
    closeColor: '#fff',
    badgeColor: '#3b9d10',
    badge: '🚀 직진형',
    title: '추진을 생각해도 좋은 달',
    body: '이 달의 리듬을 그대로 이어가면 좋아요.',
  },
  {
    key: 'inward',
    bg: '#eef4ff',
    illust: nextMonthLavenderImg,
    titleColor: '#2c2c2e',
    monthColor: '#575757',
    ctaColor: '#333',
    closeColor: '#333',
    badgeColor: '#426ebd',
    badge: '🚀 직진형',
    title: '추진을 생각해도 좋은 달',
    body: '이 달의 리듬을 그대로 이어가면 좋아요.',
  },
];

/**
 * 변형 선택 정책
 * - props.variantKey 가 있으면 해당 변형 고정
 * - 첫 오픈: 전체 변형 중 랜덤
 * - 이후 오픈: 직전 변형을 제외한 다음 인덱스로 순환 (매번 디자인이 바뀌도록)
 */
const variantIndex = ref(-1);

const pickVariantIndex = () => {
  if (props.variantKey) {
    const idx = VARIANTS.findIndex((v) => v.key === props.variantKey);
    if (idx >= 0) return idx;
  }
  if (variantIndex.value < 0) {
    return Math.floor(Math.random() * VARIANTS.length);
  }
  return (variantIndex.value + 1) % VARIANTS.length;
};

variantIndex.value = pickVariantIndex();
const variant = computed(() => VARIANTS[variantIndex.value] || VARIANTS[0]);

watch(
  () => props.modelValue,
  (open) => {
    if (open) variantIndex.value = pickVariantIndex();
  },
);

const close = () => {
  isOpen.value = false;
};

const onClickClose = () => {
  addContentLogAPI({ contentType: 'am_monthly_next_month_action_opt3', option3: '닫기' });
  close();
};

const goNext = () => {
  addContentLogAPI({ contentType: 'am_monthly_next_month_action_opt3', option3: '이동' });
  emit('goNextMonth');
  close();
};

const onKeydown = (e) => {
  if (e.key === 'Escape' && isOpen.value) close();
};
onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <Teleport to="body">
    <Transition name="mf-next-fade">
      <div v-if="isOpen" class="mf-next-backdrop" @click.self="onClickClose">
        <div class="mf-next">
          <img
            :src="variant.illust"
            alt=""
            class="mf-next__illust"
          />
          <div class="mf-next__card" :style="{ background: variant.bg }">
            <button
              type="button"
              class="mf-next__close"
              :style="{ color: variant.closeColor }"
              aria-label="닫기"
              @click="onClickClose"
            >
              <span></span>
            </button>

            <div class="mf-next__head">
              <h3 class="mf-next__title" :style="{ color: variant.titleColor }">
                다음 달, 살짝 엿보기
              </h3>
              <p class="mf-next__month" :style="{ color: variant.monthColor }">
                {{ nextMonthLabel || '6월 갑오월(甲午月)' }}
              </p>
            </div>

            <div class="mf-next__inner">
              <p class="mf-next__badge" :style="{ color: variant.badgeColor }">
                {{ dynamicBadge || variant.badge }}
              </p>
              <p class="mf-next__inner-title" v-html="nl2br(nextMonthData?.핵심메시지 || variant.title)"></p>
              <p class="mf-next__inner-body" v-html="nl2br(nextMonthData?.대비 || variant.body)"></p>
            </div>

            <button
              type="button"
              class="mf-next__cta"
              :style="{ color: variant.ctaColor, borderColor: variant.ctaColor }"
              @click="goNext"
            >
              {{ ctaText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.mf-next-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.mf-next {
  position: relative;
  width: 100%;
  max-width: 400px;
  filter: drop-shadow(0 4px 5px rgba(0, 0, 0, 0.25));
}

.mf-next__illust {
  position: absolute;
  top: -72px;
  left: 50%;
  transform: translateX(-50%);
  width: 164px;
  height: auto;
  display: block;
  z-index: 2;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.25));
}

.mf-next__card {
  position: relative;
  width: 100%;
  padding: 90px 20px 28px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  font-family: Pretendard, sans-serif;
  box-sizing: border-box;
}

@media (min-width: 480px) {
  .mf-next__card {
    padding: 100px 34px 28px;
  }
}

.mf-next__close {
  position: absolute;
  top: 17px;
  right: 14px;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  span {
    position: relative;
    width: 16px;
    height: 16px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 1.5px;
      background: currentColor;
    }
    &::before {
      transform: translateY(-50%) rotate(45deg);
    }
    &::after {
      transform: translateY(-50%) rotate(-45deg);
    }
  }
}

.mf-next__head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.mf-next__title {
  margin: 0;
  font-size: 25px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}
.mf-next__month {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
}

.mf-next__inner {
  width: 100%;
  background: #fff;
  border-radius: 10px;
  padding: 25px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  text-align: center;
  word-break: keep-all;
  overflow-wrap: break-word;
}
.mf-next__badge {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
}
.mf-next__inner-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.5;
  color: #333;
}
.mf-next__inner-body {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
  color: #333;
}

.mf-next__cta {
  background: transparent;
  border: none;
  border-bottom: 1px solid currentColor;
  padding: 0 0 3px;
  font-family: Pretendard, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
}

.mf-next-fade-enter-active,
.mf-next-fade-leave-active {
  transition: opacity 0.18s ease;
}
.mf-next-fade-enter-from,
.mf-next-fade-leave-to {
  opacity: 0;
}
</style>
